import React, { Suspense, useEffect, useState } from 'react';
import { Routes, Route } from "react-router-dom";
import { AuthProvider, useAuth } from './contexts/AuthContext';
import MomConfirmation from "./components/transaction/MomConfirmation";
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { LoginForm } from './components/auth/LoginForm';
import { DefaultLayout } from "./layout/DefaultLayout";
import { ROUTES } from './routes';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PortfolioSummaryReport from './components/reports/PortfolioSummaryReport';

// Lazy load components for code splitting
const Dashboard = React.lazy(() => import('./components/dashboard/Dashboard'));
const DesignationMaster = React.lazy(() => import('./components/systemMaster/DesignationMaster'));
const RoleMaster = React.lazy(() => import('./components/systemMaster/RoleMaster'));
const RoleRightsMaster = React.lazy(() => import('./components/systemMaster/RoleRightsMaster'));
const Lookup = React.lazy(() => import('./components/LookupMaster'));
const TemplateMaster = React.lazy(() => import('./components/systemMaster/TemplateMaster'));
const EventMaster = React.lazy(() => import('./components/systemMaster/EventMaster'));
const TriggerEventMaster = React.lazy(() => import('./components/systemMaster/TriggerEventMaster'));
const EventRuleEngineMaster = React.lazy(() => import('./components/systemMaster/EventRuleEngineMaster'));
const SectorMaster = React.lazy(() => import('./components/businessMaster/SectorMaster'));
const StockMaster = React.lazy(() => import('./components/businessMaster/StockMaster'));
const VendorMaster = React.lazy(() => import('./components/businessMaster/VendorMaster'));
const ExpenseHeadMaster = React.lazy(() => import('./components/businessMaster/ExpenseHeadMaster'));
const WorkingPartnerPayoutMaster = React.lazy(() => import('./components/businessMaster/WorkingPartnerPayoutMaster'));
const TaxMaster = React.lazy(() => import('./components/businessMaster/TaxMaster'));
const AdvanceTaxDatesMaster = React.lazy(() => import('./components/businessMaster/AdvanceTaxDatesMaster'));
const TradeDetail = React.lazy(() => import('./components/transaction/TradeDetail'));
const CapitalAddition = React.lazy(() => import('./components/transaction/CapitalAddition'));
const CapitalContribution = React.lazy(() => import('./components/transaction/CapitalContribution'));
const ClosingStock = React.lazy(() => import('./components/transaction/ClosingStock'));
const ProfitAllocation = React.lazy(() => import('./components/transaction/ProfitAllocation'));
const PartnerRemuneration = React.lazy(() => import('./components/transaction/PartnerRemuneration'));
const Expenses = React.lazy(() => import('./components/transaction/Expenses'));
const NetWorth = React.lazy(() => import('./components/transaction/NetWorth'));
const Targets = React.lazy(() => import('./components/transaction/Targets'));
const TargetPurchase = React.lazy(() => import('./components/transaction/TargetPurchase'));
const ProfitRelease = React.lazy(() => import('./components/transaction/ProfitRelease'));
const PaymentRelease = React.lazy(() => import('./components/transaction/PaymentRelease'));
const MutualFundMaster = React.lazy(() => import('./components/businessMaster/MutualFundMaster'));
const DividendReceived = React.lazy(() => import('./components/transaction/DividendReceived'));
const VendorPayout = React.lazy(() => import('./components/transaction/VendorPayout'));
const LookupMaster = React.lazy(() => import('./components/systemMaster/LookupMaster'));
const UserMaster = React.lazy(() => import('./components/systemMaster/UserMaster'));
const OpeningStockMaster = React.lazy(() => import('./components/businessMaster/OpeningStockMaster'));
const ClosingStockMaster = React.lazy(() => import('./components/businessMaster/ClosingStockMaster'));
const MomMeeting = React.lazy(() => import('./components/transaction/MomMeeting'));
const TradeReport = React.lazy(() => import('./components/reports/TradeReport'));
const NetWorthReport = React.lazy(() => import('./components/reports/NetWorthReport'));
const EquityDividendReport = React.lazy(() => import('./components/reports/EquityDividendReport'));
const MfDividendReport = React.lazy(() => import('./components/reports/MfDividendReport'));
const InflowOutflowReport = React.lazy(() => import('./components/reports/InflowOutflowReport'));
const CapitalContributionReport = React.lazy(() => import('./components/reports/CapitalContributionReport'));
const EquityPortfolioReport = React.lazy(() => import('./components/reports/EquityPortfolioReport'));
const MutualFundHoldingReport = React.lazy(() => import('./components/reports/MutualFundHoldingReport'));
const ReinvestmentReport = React.lazy(() => import('./components/reports/ReinvestmentReport'));
const ProfitSharePercentageReport = React.lazy(() => import('./components/reports/ProfitSharePercentageReport'));
const ProfitLossReport = React.lazy(() => import('./components/reports/ProfitLossReport'));
const WorkingPartnerPayoutReport = React.lazy(() => import('./components/reports/WorkingPartnerPayoutReport'));
const UploadScreen = React.lazy(() => import('./components/transaction/UploadScreen'));
const UserProfile = React.lazy(() => import('./components/profile/UserProfile'));
const MonthlyReport = React.lazy(() => import('./components/reports/MonthlyReport'));
const PartnerDetailsReport = React.lazy(() => import('./components/reports/PartnerDetailsReport'));
const ProfitReleaseReport = React.lazy(() => import('./components/reports/ProfitReleaseReport'));
const TargetReport = React.lazy(() => import('./components/reports/TargetReport'));
const NomineeReport = React.lazy(() => import('./components/reports/NomineeReport'));
const AnnualReport = React.lazy(() => import('./components/reports/AnnualReport'));
const MOMReport = React.lazy(() => import('./components/reports/MOMReport'));

function MomConfirmationController() {
  const { isAuthenticated } = useAuth();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isAuthenticated) setShow(true);
  }, [isAuthenticated]);

  if (!isAuthenticated) return null;
  return <MomConfirmation visible={show} onDone={() => setShow(false)} />;
}

function LoadingSpinner() {
  return <div className="loading-spinner">Loading...</div>;
}

function AppContent() {
  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <MomConfirmationController />
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path={ROUTES.DEFAULT_LOGIN} element={<LoginForm />} />
          <Route path={ROUTES.LOGIN} element={<LoginForm />} />
          <Route element={<ProtectedRoute><DefaultLayout /></ProtectedRoute>}>
            <Route path={ROUTES.DASHBOARD} element={<Dashboard />} />
            <Route path={ROUTES.DESIGNATION} element={<DesignationMaster />} />
            <Route path={ROUTES.ROLE} element={<RoleMaster />} />
            <Route path={ROUTES.LOOKUP} element={<Lookup />} />
            <Route path={ROUTES.TEMPLATE} element={<TemplateMaster />} />
            <Route path={ROUTES.EVENT} element={<EventMaster />} />
            <Route path={ROUTES.TRIGGER_EVENT} element={<TriggerEventMaster />} />
            <Route path={ROUTES.EVENT_RULE} element={<EventRuleEngineMaster />} />
            <Route path={ROUTES.SECTOR} element={<SectorMaster />} />
            <Route path={ROUTES.STOCK} element={<StockMaster />} />
            <Route path={ROUTES.VENDOR} element={<VendorMaster />} />
            <Route path={ROUTES.EXPENSE_HEAD} element={<ExpenseHeadMaster />} />
            <Route path={ROUTES.WORKING_PARTNER_PAYOUT} element={<WorkingPartnerPayoutMaster />} />
            <Route path={ROUTES.TAX_MASTER} element={<TaxMaster />} />
            <Route path={ROUTES.ADVANCE_TAX_DATE} element={<AdvanceTaxDatesMaster />} />
            <Route path={ROUTES.TRADE_DETAIL} element={<TradeDetail />} />
            <Route path={ROUTES.CAPITAL_ADDITION} element={<CapitalAddition />} />
            <Route path={ROUTES.CAPITAL_CONTRIBUTION} element={<CapitalContribution />} />
            <Route path={ROUTES.CLOSING_STOCK} element={<ClosingStock />} />
            <Route path={ROUTES.PROFIT_ALLOCATION} element={<ProfitAllocation />} />
            <Route path={ROUTES.PARTNER_REMUNERATION} element={<PartnerRemuneration />} />
            <Route path={ROUTES.EXPENSES} element={<Expenses />} />
            <Route path={ROUTES.NET_WORTH} element={<NetWorth />} />
            <Route path={ROUTES.TARGETS} element={<Targets />} />
            <Route path={ROUTES.TARGET_PURCHASE} element={<TargetPurchase />} />
            <Route path={ROUTES.PROFIT_RELEASE} element={<ProfitRelease />} />
            <Route path={ROUTES.PAYMENT_RELEASE} element={<PaymentRelease />} />
            <Route path={ROUTES.MUTUAL_FUNDS} element={<MutualFundMaster />} />
            <Route path={ROUTES.DIVIDEND} element={<DividendReceived />} />
            <Route path={ROUTES.VENDOR_PAYOUT} element={<VendorPayout />} />
            <Route path={ROUTES.LOOKUP_MASTER} element={<LookupMaster />} />
            <Route path={ROUTES.USER_MASTER} element={<UserMaster />} />
            <Route path={ROUTES.ROLE_RIGHTS} element={<RoleRightsMaster />} />
            <Route path={ROUTES.OPENING_MONTH_STOCK} element={<OpeningStockMaster />} />
            <Route path={ROUTES.CLOSING_MONTH_STOCK} element={<ClosingStockMaster />} />
            <Route path={ROUTES.MOMS} element={<MomMeeting />} />
            <Route path={ROUTES.TRADE_REPORT} element={<TradeReport />} />
            <Route path={ROUTES.NET_WORTH_REPORT} element={<NetWorthReport />} />
            <Route path={ROUTES.EQUITY_DIVIDENT_REPORT} element={<EquityDividendReport />} />
            <Route path={ROUTES.MF_DIVIDEND_REPORT} element={<MfDividendReport />} />
            <Route path={ROUTES.IN_FLOW_OUTFLOW_REPORT} element={<InflowOutflowReport />} />
            <Route path={ROUTES.CAPITAL_CONTRIBUTION_REPORT} element={<CapitalContributionReport />} />
            <Route path={ROUTES.WORKING_PARTNER_PAYOUT_REPORT} element={<WorkingPartnerPayoutReport />} />
            <Route path={ROUTES.EQUITY_PORTFOLIO_REPORT} element={<EquityPortfolioReport />} />
            <Route path={ROUTES.MUTUAL_FUND_HOLDING_REPORT} element={<MutualFundHoldingReport />} />
            <Route path={ROUTES.REINVESTMENT_REPORT} element={<ReinvestmentReport />} />
            <Route path={ROUTES.PROFIT_SHARE_PERCENTAGE_REPORT} element={<ProfitSharePercentageReport />} />
            <Route path={ROUTES.PROFIT_LOSS_REPORT} element={<ProfitLossReport />} />
            <Route path={ROUTES.PORTFOLIO_SUMMARY_REPORT} element={<PortfolioSummaryReport />} />
            <Route path={ROUTES.UPLOAD} element={<UploadScreen />} />
            <Route path={ROUTES.USER_PROFILE} element={<UserProfile />} />
            <Route path={ROUTES.MONTHLY_REPORT} element={<MonthlyReport />} />
            <Route path={ROUTES.PARTNER_DETAILS_REPORT} element={<PartnerDetailsReport />} />
            <Route path={ROUTES.PROFIT_RELEASE_REPORT} element={<ProfitReleaseReport />} />
            <Route path={ROUTES.TARGET_REPORT} element={<TargetReport />} />
            <Route path={ROUTES.NOMINEE_REPORT} element={<NomineeReport />} />
            <Route path={ROUTES.ANNUAL_REPORT} element={<AnnualReport />} />
            <Route path={ROUTES.MOM_REPORT} element={<MOMReport />} />
          </Route>
        </Routes>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;